import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  getClientPortalSession,
  getMe,
  loginClient,
  logoutClientPortal,
} from "../api/clientPortalPublic";
import { CLIENT_SESSION_TOKEN } from "../api/base";

const ClientPortalAuthContext = createContext(null);

export function ClientPortalAuthProvider({ salonId, children }) {
  const [token, setToken] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(false);

  const clientLogin = useCallback(
    async (payload) => {
      if (!salonId) {
        throw new Error("Salon is required to login");
      }
      setLoading(true);
      try {
        const response = await loginClient(salonId, payload);
        setToken(CLIENT_SESSION_TOKEN);
        const me = await getMe(CLIENT_SESSION_TOKEN);
        setClient(me || null);
        return response;
      } finally {
        setLoading(false);
      }
    },
    [salonId]
  );

  const clientLogout = useCallback(() => {
    setToken(null);
    setClient(null);
    void logoutClientPortal().catch(() => {});
  }, []);

  const clientMe = useCallback(async () => {
    if (!token) {
      setClient(null);
      return null;
    }
    setLoading(true);
    try {
      const data = await getMe(token);
      setClient(data || null);
      return data;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    let cancelled = false;

    async function hydrateSession() {
      if (!salonId) {
        setToken(null);
        setClient(null);
        return;
      }

      setLoading(true);
      try {
        const session = await getClientPortalSession();
        if (!session || Number(session.salon_id) !== Number(salonId) || cancelled) {
          setToken(null);
          setClient(null);
          return;
        }
        setToken(CLIENT_SESSION_TOKEN);
        const me = await getMe(CLIENT_SESSION_TOKEN).catch(() => null);
        if (!cancelled) {
          setClient(me || null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    hydrateSession();
    return () => {
      cancelled = true;
    };
  }, [salonId]);

  const value = useMemo(
    () => ({
      salonId,
      token,
      client,
      loading,
      setClient,
      clientLogin,
      clientLogout,
      clientMe,
    }),
    [salonId, token, client, loading, clientLogin, clientLogout, clientMe]
  );

  return (
    <ClientPortalAuthContext.Provider value={value}>
      {children}
    </ClientPortalAuthContext.Provider>
  );
}

export function useClientPortalAuth() {
  const ctx = useContext(ClientPortalAuthContext);
  if (!ctx) {
    throw new Error("useClientPortalAuth must be used within ClientPortalAuthProvider");
  }
  return ctx;
}
